import React, { useMemo, useState } from 'react';

import { TaskDetail } from '@/components/tasks/task-detail';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import { useGetMattersQuery } from '@/services/api/mattersService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { BaseMatter, Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { Day1FirmGroup } from './Day1FirmGroup';
import { Day1StatsOverview } from './Day1StatsOverview';

interface MatterWithTasks extends BaseMatter {
  tasks: Task[];
  completedTasksCount: number;
  pendingTasksCount: number;
  urgentTasksCount: number;
  isDay1Matter: boolean;
  day1QualifyingGroups: CanonicalTaskGroup[];
  isNewMatter: boolean;
}

interface FirmGroup {
  firm: { id: string; name: string };
  matters: MatterWithTasks[];
  totalMatters: number;
  urgentCount: number;
  completedCount: number;
  pendingCount: number;
}

// Day 1 criteria definition
const DAY1_CRITERIA = [
  {
    group: CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
    types: [CanonicalTaskType.SEND_LOR, CanonicalTaskType.OPEN_CLAIM],
  },
  {
    group: CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
    types: [CanonicalTaskType.SEND_LOR, CanonicalTaskType.OPEN_CLAIM],
  },
  {
    group: CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION,
    types: [], // Any type in this group
  },
];

export const Day1Page: React.FC = () => {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const { data: mattersData, isLoading: mattersLoading } = useGetMattersQuery();
  const matters = mattersData?.results;

  // Extract all Day 1 task groups and types for API filtering
  const day1TaskGroups = DAY1_CRITERIA.map((criteria) => criteria.group);
  const day1TaskTypes = [
    ...new Set(DAY1_CRITERIA.flatMap((criteria) => criteria.types)),
  ]; // Deduplicate types

  // For now, let's fetch all tasks and filter client-side since we have mixed criteria
  // (some groups need specific types, others need any type)
  const { data: taskResponse, isLoading: tasksLoading } = useGetTasksQuery({
    limit: 1000,
    ordering: '-modified_at',
  });

  // Helper function to check if a task qualifies for Day 1
  const isDay1Task = (task: Task): boolean => {
    const result = DAY1_CRITERIA.some((criteria) => {
      const matchesGroup = task.group?.canonical_group === criteria.group;
      if (!matchesGroup) return false;

      // If no specific types are specified for this group, any type qualifies
      if (criteria.types.length === 0) return true;

      // Otherwise, check if the task type matches one of the specified types
      return criteria.types.includes(task.type?.canonical_type);
    });

    return result;
  };

  // Helper function to check if a task is outstanding (not completed/not applicable)
  const isOutstandingTask = (task: Task): boolean => {
    return (
      task.status.canonical_status !== CanonicalTaskStatus.COMPLETED &&
      task.status.canonical_status !== CanonicalTaskStatus.NOT_APPLICABLE
    );
  };

  // Get all Day 1 tasks that are outstanding
  const outstandingDay1Tasks = useMemo(() => {
    if (!taskResponse?.results) return [];
    return taskResponse.results.filter(
      (task) => isDay1Task(task) && isOutstandingTask(task),
    );
  }, [taskResponse]);

  // Get all tasks (for display purposes)
  const allTasks = useMemo(() => {
    if (!taskResponse?.results) return [];

    return taskResponse.results;
  }, [taskResponse]);

  // Identify Day 1 matters based on outstanding Day 1 tasks
  const day1MatterIds = useMemo(() => {
    return new Set(outstandingDay1Tasks.map((task) => task.matter));
  }, [outstandingDay1Tasks]);

  // Filter and enhance matters with Day 1 information
  const day1Matters = useMemo(() => {
    if (!matters) return [];

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const filtered = matters
      .filter((matter) => day1MatterIds.has(matter.id))
      .map((matter) => {
        // Check which Day 1 groups this matter qualifies for
        const qualifyingGroups = DAY1_CRITERIA.filter((criteria) =>
          outstandingDay1Tasks.some(
            (task) =>
              task.matter === matter.id &&
              task.group?.canonical_group === criteria.group &&
              (criteria.types.length === 0 ||
                criteria.types.includes(task.type?.canonical_type)),
          ),
        ).map((criteria) => criteria.group);

        // Check if this is a newly created matter
        const matterDate = matter.created_at
          ? new Date(matter.created_at)
          : matter.modified_at
            ? new Date(matter.modified_at)
            : null;
        const isNewMatter = matterDate && matterDate >= sevenDaysAgo;

        return {
          ...matter,
          isDay1Matter: true,
          day1QualifyingGroups: qualifyingGroups,
          isNewMatter: !!isNewMatter,
        };
      })
      .sort((a, b) => {
        // Sort new matters first, then by modification date
        if (a.isNewMatter && !b.isNewMatter) return -1;
        if (!a.isNewMatter && b.isNewMatter) return 1;

        const aDate = new Date(a.modified_at || 0);
        const bDate = new Date(b.modified_at || 0);
        return bDate.getTime() - aDate.getTime();
      });

    return filtered;
  }, [matters, day1MatterIds, outstandingDay1Tasks]);

  // Group matters by firm and add task information
  const firmGroups = useMemo(() => {
    if (!day1Matters || !allTasks) return [];

    const matterTasksMap = new Map<string, Task[]>();

    // Group ALL tasks by matter ID (we want to show all tasks, not just Day 1 tasks)
    allTasks.forEach((task: Task) => {
      if (day1MatterIds.has(task.matter)) {
        if (!matterTasksMap.has(task.matter)) {
          matterTasksMap.set(task.matter, []);
        }
        matterTasksMap.get(task.matter)!.push(task);
      }
    });

    // Create matters with task information
    const mattersWithTasks: MatterWithTasks[] = day1Matters.map((matter) => {
      const matterTasks = matterTasksMap.get(matter.id) || [];
      const completedTasks = matterTasks.filter(
        (task) =>
          task.status.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
      const pendingTasks = matterTasks.filter(
        (task) =>
          task.status.canonical_status !== CanonicalTaskStatus.COMPLETED,
      );
      const urgentTasks = matterTasks.filter((task) => task.order === 1);

      return {
        ...matter,
        tasks: matterTasks,
        completedTasksCount: completedTasks.length,
        pendingTasksCount: pendingTasks.length,
        urgentTasksCount: urgentTasks.length,
      };
    });

    // Group by firm
    const firmMap = new Map<string, MatterWithTasks[]>();
    mattersWithTasks.forEach((matter) => {
      const firmId = matter.firm.id;
      if (!firmMap.has(firmId)) {
        firmMap.set(firmId, []);
      }
      firmMap.get(firmId)!.push(matter);
    });

    // Convert to firm groups with aggregated stats
    return Array.from(firmMap.entries()).map(([firmId, firmMatters]) => ({
      firm: firmMatters[0].firm,
      matters: firmMatters,
      totalMatters: firmMatters.length,
      urgentCount: firmMatters.reduce(
        (sum, matter) => sum + matter.urgentTasksCount,
        0,
      ),
      completedCount: firmMatters.reduce(
        (sum, matter) => sum + matter.completedTasksCount,
        0,
      ),
      pendingCount: firmMatters.reduce(
        (sum, matter) => sum + matter.pendingTasksCount,
        0,
      ),
    }));
  }, [day1Matters, allTasks, day1MatterIds]);

  const totalMatters = day1Matters.length;
  const totalUrgentTasks = firmGroups.reduce(
    (sum, group) => sum + group.urgentCount,
    0,
  );
  const totalCompletedTasks = firmGroups.reduce(
    (sum, group) => sum + group.completedCount,
    0,
  );
  const totalPendingTasks = firmGroups.reduce(
    (sum, group) => sum + group.pendingCount,
    0,
  );

  // Show loading if matters are loading or tasks are loading
  const isLoading = mattersLoading || tasksLoading;

  const handleTaskClick = (taskId: string) => {
    // Find the task in allTasks
    const task = allTasks?.find((t) => t.id === taskId);
    if (task) {
      setSelectedTask(task);
    }
  };

  const handleCloseTaskModal = () => {
    setSelectedTask(null);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-6" />
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Day 1 Tasks</h1>
        <p className="mt-2 text-gray-600">
          Critical tasks for new matters requiring immediate attention
        </p>

        {/* Overview Stats */}
        <Day1StatsOverview
          totalMatters={totalMatters}
          totalCompletedTasks={totalCompletedTasks}
          totalPendingTasks={totalPendingTasks}
          totalUrgentTasks={totalUrgentTasks}
        />
      </div>

      <div className="space-y-6">
        {firmGroups.length > 0 ? (
          firmGroups.map((group) => (
            <Day1FirmGroup
              key={group.firm.id}
              firmGroup={group}
              onTaskClick={handleTaskClick}
              isDay1Task={isDay1Task}
            />
          ))
        ) : (
          <Card className="p-8 text-center">
            <div className="text-gray-500">
              <p className="mb-2 text-lg">No Day 1 matters found</p>
              <p className="text-sm">
                Matters with relevant statuses created within the last 7 days
                will appear here.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <Dialog open onOpenChange={handleCloseTaskModal} modal={false}>
          <RightDialogContent className="bg-white h-[95vh] overflow-y-scroll flex flex-col gap-4">
            <DialogTitle className="sr-only">Task Details</DialogTitle>
            <TaskDetail
              taskId={selectedTask.id}
              matterId={selectedTask.matter}
              onClose={handleCloseTaskModal}
              isModal={false} // Use the non-modal version inside our custom dialog
            />
          </RightDialogContent>
        </Dialog>
      )}
    </div>
  );
};
